(() => {
  function initScene(root, motion) {
    const scene = root.querySelector('[data-motion-scene]');
    if (!scene) return;
    const button = scene.querySelector('[data-motion-toggle]');
    let paused = motion.matches;
    function sync() {
      scene.classList.toggle('npc-scene--paused', paused);
      button?.setAttribute('aria-pressed', String(paused));
      if (button) button.textContent = paused ? button.dataset.play : button.dataset.pause;
    }
    sync();
    button?.addEventListener('click', () => {paused = !paused; sync();});
    motion.addEventListener('change', () => {paused = motion.matches; sync();});
    let inView = true, scheduled = false;
    if ('IntersectionObserver' in window) new IntersectionObserver(entries => {
      inView = entries[0].isIntersecting;
      scene.classList.toggle('npc-scene--offscreen', !inView);
    }).observe(scene);
    function update() {
      scheduled = false;
      if (!scene.isConnected || paused || motion.matches || !inView) return;
      const rect = scene.getBoundingClientRect();
      const shift = Math.max(-1, Math.min(1, (innerHeight / 2 - rect.top - rect.height / 2) / innerHeight));
      scene.style.setProperty('--scene-scroll', (shift * 22).toFixed(2) + 'px');
    }
    window.addEventListener('scroll', () => {
      if (!scheduled && inView && !paused) {scheduled = true; requestAnimationFrame(update);}
    }, {passive:true});
    update();
  }
  function init(root) {
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    root.classList.add('npc--enhanced');
    const form = root.querySelector('form');
    const panel = root.querySelector('[data-collection-filters]');
    const toggle = root.querySelector('[data-collection-filter]');
    const grid = root.querySelector('[data-collection-grid]');
    const pagination = root.querySelector('[data-collection-pagination]');
    const configNode = root.querySelector('[data-collection-pagination-config]');
    const config = configNode ? JSON.parse(configNode.textContent) : null;
    function progress() {
      const shown = Number(config.offset) + grid.querySelectorAll('[data-collection-product]').length;
      root.querySelector('[data-collection-progress]').textContent = config.progress.replace('[shown]', shown).replace('[total]', config.total);
    }
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let observer;
    function revealCards() {
      if (!('IntersectionObserver' in window) || motion.matches) return;
      if (!observer) observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, {threshold: 0.04});
      root.querySelectorAll('[data-collection-reveal]:not([data-reveal-ready])').forEach((card, index) => {
        card.dataset.revealReady = 'true';
        card.style.setProperty('--npc-delay', String(Math.min(index % 4, 3) * 45) + 'ms');
        card.classList.add('npc-reveal');
        observer.observe(card);
      });
    }
    revealCards();
    initScene(root, motion);
    if (!grid || !pagination || !config) return;
    progress();
    toggle?.addEventListener('click', () => {
      const open = panel.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
    });
    root.querySelector('[data-collection-sort]')?.addEventListener('change', () => form.requestSubmit());
    let busy = false;
    pagination.addEventListener('click', async event => {
      const link = event.target.closest('[data-collection-more]');
      if (!link) return;
      event.preventDefault();
      if (busy) return;
      busy = true;
      const status = root.querySelector('[data-collection-status]');
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin) { busy = false; return; }
      url.searchParams.set('section_id', root.dataset.sectionId);
      link.setAttribute('aria-disabled', 'true');
      link.textContent = root.dataset.loadingLabel;
      status.textContent = '';
      try {
        const response = await fetch(url, {signal: AbortSignal.timeout(15000)});
        if (!response.ok) throw new Error('Products unavailable');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextRoot = page.getElementById(root.id);
        const items = nextRoot?.querySelectorAll('[data-collection-product]');
        if (!nextRoot || !items.length) throw new Error('Invalid collection response');
        const ids = new Set([...grid.querySelectorAll('[data-collection-product]')].map(item => item.dataset.collectionProduct));
        const fragment = document.createDocumentFragment();
        for (const item of items) {
          if (!ids.has(item.dataset.collectionProduct)) {fragment.append(item);ids.add(item.dataset.collectionProduct);}
        }
        const x = window.scrollX, y = window.scrollY;
        grid.append(fragment);
        revealCards();
        const next = nextRoot.querySelector('[data-collection-more]');
        if (next) {
          const nextUrl = new URL(next.getAttribute('href'), location.href);
          nextUrl.searchParams.delete('section_id');
          link.href = nextUrl.href;
        }
        else link.remove();
        progress();
        window.scrollTo({left:x,top:y,behavior:'instant'});
      } catch {
        status.textContent = root.dataset.errorLabel;
      } finally {
        busy = false;
        link.removeAttribute('aria-disabled');
        link.textContent = root.dataset.moreLabel;
      }
    });
    const current = new URL(location.href);
    if (root.dataset.defaultSort && !current.searchParams.has('sort_by')) {
      const sorted = new URL(current);
      sorted.searchParams.set('sort_by', root.dataset.defaultSort);
      const request = new URL(sorted);
      request.searchParams.set('section_id', root.dataset.sectionId);
      root.setAttribute('aria-busy', 'true');
      fetch(request, {signal:AbortSignal.timeout(15000)}).then(response => {
        if (!response.ok) throw new Error('Sort unavailable');
        return response.text();
      }).then(html => {
        if (!root.isConnected || location.href !== current.href) return;
        const nextRoot = new DOMParser().parseFromString(html, 'text/html').getElementById(root.id);
        if (!nextRoot) throw new Error('Invalid collection response');
        history.replaceState(history.state, '', sorted);
        root.replaceWith(nextRoot);
        init(nextRoot);
      }).catch(() => root.removeAttribute('aria-busy'));
    }
  }
  function start() {document.querySelectorAll('[data-premium-collection]').forEach(init);}
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
